/** Shared display formatters for the admin portal. */

/**
 * Format a UKC amount with 8 decimal places and locale-aware thousands separator.
 * Handles string values from the API (Prisma Decimal serializes to string).
 */
export function formatUkc(value: string | number | null | undefined): string {
  if (value == null || value === '') return '—';
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(n)) return '—';
  return (
    n.toLocaleString('en-GB', {
      minimumFractionDigits: 8,
      maximumFractionDigits: 8,
    }) + ' UKC'
  );
}

/**
 * Format a UKC amount with a K/M/B suffix for compact display (e.g. in stat cards).
 */
export function formatUkcAbbreviated(value: string | number | null | undefined): string {
  if (value == null || value === '') return '—';
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return (n / 1_000_000_000).toFixed(2) + 'B UKC';
  if (abs >= 1_000_000) return (n / 1_000_000).toFixed(2) + 'M UKC';
  if (abs >= 1_000) return (n / 1_000).toFixed(2) + 'K UKC';
  return n.toFixed(8) + ' UKC';
}

/**
 * Format a generic currency amount with an optional symbol suffix.
 * Used for world-native currencies where "UKC" isn't the label.
 */
export function formatAmount(
  value: string | number | null | undefined,
  symbol?: string,
): string {
  if (value == null || value === '') return '—';
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(n)) return '—';
  const formatted = n.toLocaleString('en-GB', {
    minimumFractionDigits: 8,
    maximumFractionDigits: 8,
  });
  return symbol ? `${formatted} ${symbol}` : formatted;
}

/**
 * Format a percentage value to 2 decimal places.
 * Input may be 0–100 (e.g. "30.00") or 0–1 (e.g. "0.30") — pass asDecimalFraction=true for the latter.
 */
export function formatPercent(
  value: string | number | null | undefined,
  asDecimalFraction = false,
): string {
  if (value == null || value === '') return '—';
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(n)) return '—';
  const pct = asDecimalFraction ? n * 100 : n;
  return pct.toFixed(2) + '%';
}

/**
 * Format a date as "09 Mar 2026".
 */
export function formatDate(value: string | Date | null | undefined): string {
  if (value == null || value === '') return '—';
  try {
    return new Date(value).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

/**
 * Format a date + time as "09 Mar 2026, 14:35".
 */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (value == null || value === '') return '—';
  try {
    return new Date(value).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}
